import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { base64ToArrayBuffer, unwrapKeyFromSender } from '../lib/crypto';
import { FEED_KEY_VERSION, decryptPost, encryptPost, getOrCreateFeedKey } from '../lib/feed-key';
import { t } from '../lib/i18n';
import { api } from '../lib/trpc/client';
import { useAuth } from '../hooks';

type TimelinePost = {
  postId: string;
  authorId: string;
  authorUsername: string;
  timestamp: string;
  keyVersion: string;
  content: string;
};

export function TimelinePage() {
  const navigate = useNavigate();
  const { userId, checkSession } = useAuth();
  const [posts, setPosts] = useState<TimelinePost[]>([]);
  const [draft, setDraft] = useState('');
  const [checkingSession, setCheckingSession] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPosts = useCallback(async () => {
    if (!userId) {
      return;
    }

    setLoadingPosts(true);
    setError(null);

    try {
      const response = await api.feed.getTimeline({ limit: 50 });
      const activeCircle = await api.circle.getActiveCircle();
      if (!activeCircle) {
        navigate('/circles', { replace: true });
        return;
      }
      const memberResult = await api.feed.getCircleMembers();
      const memberNameById = new Map(memberResult.members.map((member) => [member.userId, member.scratchUsername]));
      const keyCache = new Map<string, CryptoKey>();
      const ownerId = activeCircle.ownerUserId;

      const resolveCircleKey = async (version: string): Promise<CryptoKey> => {
        const cacheKey = version;
        const cached = keyCache.get(cacheKey);
        if (cached) {
          return cached;
        }

        if (userId === ownerId) {
          const ownKey = await getOrCreateFeedKey(version);
          keyCache.set(cacheKey, ownKey);
          return ownKey;
        }

        const identitySkRaw = localStorage.getItem('identitySkRaw');
        if (!identitySkRaw) {
          throw new Error('Missing local identity key for invite decryption');
        }

        const keyResult = await api.feed.getFeedKeys({ userId });
        const encrypted = keyResult.encryptedKeys.find((item) => item.version === version && item.ephemeralPk);
        if (!encrypted || !encrypted.ephemeralPk) {
          throw new Error(`No shared feed key for ${version}`);
        }

        const unwrapped = await unwrapKeyFromSender(
          base64ToArrayBuffer(encrypted.ciphertext),
          base64ToArrayBuffer(encrypted.ephemeralPk),
          base64ToArrayBuffer(identitySkRaw)
        );
        keyCache.set(cacheKey, unwrapped);
        return unwrapped;
      };

      const decrypted = await Promise.all(
        response.posts.map(async (post): Promise<TimelinePost> => {
          try {
            const content = await decryptPost(post.ciphertext, await resolveCircleKey(post.keyVersion));
            return {
              postId: post.postId,
              authorId: post.authorId,
              authorUsername: memberNameById.get(post.authorId) ?? post.authorId,
              timestamp: post.timestamp,
              keyVersion: post.keyVersion,
              content
            };
          } catch {
            return {
              postId: post.postId,
              authorId: post.authorId,
              authorUsername: memberNameById.get(post.authorId) ?? post.authorId,
              timestamp: post.timestamp,
              keyVersion: post.keyVersion,
              content: t('[復号失敗]', '[decrypt failed]')
            };
          }
        })
      );

      setPosts(decrypted);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('投稿の取得に失敗しました', 'Failed to load posts'));
    } finally {
      setLoadingPosts(false);
    }
  }, [navigate, userId]);

  useEffect(() => {
    const run = async () => {
      await checkSession();
      setCheckingSession(false);
    };

    void run();
  }, [checkSession]);

  useEffect(() => {
    if (!checkingSession && userId) {
      void loadPosts();
    }
  }, [checkingSession, userId, loadPosts]);

  const handleCreatePost = async () => {
    const normalized = draft.trim();
    if (!userId || !normalized) {
      return;
    }

    setPosting(true);
    setError(null);

    try {
      const activeCircle = await api.circle.getActiveCircle();
      if (!activeCircle) {
        navigate('/circles', { replace: true });
        return;
      }

      let feedKey: CryptoKey;
      const memberResult = await api.feed.getCircleMembers();
      if (userId === activeCircle.ownerUserId) {
        feedKey = await getOrCreateFeedKey(FEED_KEY_VERSION);
      } else {
        const identitySkRaw = localStorage.getItem('identitySkRaw');
        if (!identitySkRaw) {
          throw new Error(t('ローカル秘密鍵がありません', 'Local identity key missing'));
        }

        const keyResult = await api.feed.getFeedKeys({ userId });
        const encrypted = keyResult.encryptedKeys.find((item) => item.version === FEED_KEY_VERSION && item.ephemeralPk);
        if (!encrypted || !encrypted.ephemeralPk) {
          throw new Error(t('共有鍵が見つかりません', 'Shared key not found'));
        }

        feedKey = await unwrapKeyFromSender(
          base64ToArrayBuffer(encrypted.ciphertext),
          base64ToArrayBuffer(encrypted.ephemeralPk),
          base64ToArrayBuffer(identitySkRaw)
        );
      }

      const ciphertext = await encryptPost(normalized, feedKey);
      const timestamp = new Date().toISOString();
      const result = await api.feed.createPost({
        ciphertext,
        keyVersion: FEED_KEY_VERSION,
        timestamp
      });

      setDraft('');
      setPosts((prev) => [
        {
          postId: result.postId,
          authorId: userId,
          authorUsername: memberResult.members.find((member) => member.userId === userId)?.scratchUsername ?? userId,
          timestamp,
          keyVersion: FEED_KEY_VERSION,
          content: normalized
        },
        ...prev
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('投稿の作成に失敗しました', 'Failed to create post'));
    } finally {
      setPosting(false);
    }
  };

  if (checkingSession) {
    return (
      <main className="mx-auto flex min-h-screen w-full max-w-2xl flex-col gap-4 px-4 py-10">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-28 w-full" />
      </main>
    );
  }

  if (!userId) {
    return (
      <main className="flex min-h-screen items-center justify-center px-4 py-10">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t('タイムラインを見るにはログインが必要です', 'You need to log in to view the timeline')}</CardTitle>
            <CardDescription>{t('セッションが見つかりませんでした。', 'No active session was found.')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/login">{t('ログインへ', 'Go to login')}</Link>
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  return (
    <main className="mx-auto min-h-screen w-full max-w-2xl space-y-4 px-4 py-10">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold">{t('タイムライン', 'Timeline')}</h1>
        <p className="text-sm text-muted-foreground">{t('投稿は端末で暗号化されてから保存されます。', 'Posts are encrypted on your device before they are stored.')}</p>
      </header>

      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('新規投稿', 'New Post')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder={t('いま何を考えていますか？', "What's on your mind?")}
            className="min-h-28"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={2000}
          />
          <Button className="mt-3" disabled={posting || !draft.trim()} onClick={handleCreatePost}>
            {posting ? t('投稿中...', 'Posting...') : t('投稿', 'Post')}
          </Button>
        </CardContent>
      </Card>

      {loadingPosts ? (
        <div className="space-y-3">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
      ) : posts.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {t('投稿がまだありません。サークル内の投稿がここに表示されます。', 'No posts yet. Posts from your circle will appear here.')}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <Card key={post.postId}>
              <CardHeader className="space-y-1 pb-3">
                <CardTitle className="text-base">{new Date(post.timestamp).toLocaleString()}</CardTitle>
                <CardDescription>{post.authorUsername} / {t('鍵', 'Key')}: {post.keyVersion}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{post.content}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </main>
  );
}
